import { ActionStatusHelper, depByTouch } from "@/Shared/Helpers";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DOMAIN_CONFIGS, DomainHookProps, useDomainActions } from "@/Shared";
import { ActionResponse, DataTableConfig, DomainTableConfig, DomainType, Entity, EntityHook, Header } from "@/Shared/Types";

/**
 * useLayoutLifeCycle hook
 *
 * Generic lifecycle orchestration hook for domain layouts.
 * Acts as the orchestration layer between:
 * - domain data access and resolution,
 * - permissions and domain-level actions,
 * - table configuration construction,
 * - and UI state synchronization.
 *
 * Responsibilities:
 * - Guard initialization based on combined response status.
 * - Resolve domain data and transform models into ViewModels.
 * - Build and expose a unified DataTable configuration.
 * - Centralize delete and restore workflows with feedback handling.
 * - Expose a stable, memoized API to the layout layer.
 *
 * @date: March 2, 2026
 * @author: Samano CASTRE
 * 
 * @see DomainHookProps
 * @see DomainTableConfig<ViewModel>
 *
 * @param config Layout lifecycle configuration
 * @returns DomainTableConfig<ViewModel>
 */
export const useLayoutLifeCycle = <ViewModel extends Record<string, any>, Model extends Entity>(
    config: {
        response: ActionResponse,
        domainType: DomainType,
        props: DomainHookProps,
        entityHook: EntityHook<Model>,
        headers: Record<keyof ViewModel, Header<ViewModel>>,
        toViewModelFn: (model: Model, isDisplaying : boolean) => ViewModel,
        onSuccess?: () => Promise<void>,
        deps?: (string | boolean | number)[]
    }
) => {

    const {
        response,
        domainType,
        props,
        entityHook,
        headers,
        toViewModelFn,
        deps
    } = config;

    const { extraArgs, accessRight, uiFeedbackHooks } = props;
    const { selfBadges } = extraArgs ?? {};
    const { domainLabel } = DOMAIN_CONFIGS[domainType];

    /**
     * Entity domain operations and state
     */
    const {
        queryId,
        dataMap,
        deleteAction,
        restoreAction,
        generateAction,
        exportAction,
        curEntityMode,
        toggleEntityMode,
    } = entityHook;

    /**
     * Domain-level actions and permissions
     */
    const {
        userActions,
        isDisplaying,
        exportHook
    } = useDomainActions({
        domainType,
        curEntityMode,
        toggleEntityMode,
        deleteAction,
        restoreAction,
        generateAction,
        exportAction,
        uiFeedbackHooks,
        accessRight
    });

    /**
     * Memoized displaying resolver
     */
    const displaying = useCallback(isDisplaying, [curEntityMode, queryId]);


    /**
     * Local state holding the resolved table configuration
     */
    const [entityTableConfig, setEntityTableConfig] = useState<DataTableConfig<ViewModel>>();


    /**
     * Builds the table configuration once all
     * required data sources are ready.
     */
    useEffect(() => {
        if (ActionStatusHelper.isSucceeded(response.status)) {

            setEntityTableConfig({
                domainLabel,

                /**
                 * Headers are built dynamically using
                 * property enums.
                 */
                headers,

                /**
                 * Domain models are enriched, mapped to
                 * view models and sorted before rendering.
                 */
                initialData: dataMap.toArray().map(property => 
                    toViewModelFn(
                        property, 
                        displaying(property.id, queryId)
                    )
                ),

                /**
                 * User actions fully resolved at the domain level.
                 */
                userActions,
                isRestorable: accessRight.canRestore(domainType) && !DOMAIN_CONFIGS[domainType].desableDelete,
                newItems: selfBadges
            } as DataTableConfig<ViewModel>);

        }
    }, [
        response.status,
        depByTouch(dataMap),
        queryId,
        displaying,
        selfBadges,
        ...(deps ?? [])
    ]);

    /**
     * Memoized API exposed to the layout layer
     */
    const result = useMemo(() => ({
        tableConfig: entityTableConfig,
        exportHook,
        response,
        deleteResponse: deleteAction.response,
        setDeleteResponse: deleteAction.setResponse,

        generateResponse: generateAction.response,
        setGenerateResponse: generateAction.setResponse,

        restoreResponse: restoreAction.response,
        setRestoreResponse: restoreAction.setResponse,

    } as DomainTableConfig<ViewModel>),
        [
            entityTableConfig,
            deleteAction.response,
            restoreAction.response,
            generateAction.response,
            response,
            exportHook.exportDocumentResponse,
        ]);

    return result;
}